// Småt: små gæster på nettet, når der er stille. En stork, en drage, en ko eller (om vinteren) et
// juletræ lander på en ledning. Et tap jager den væk for 1 kr. Får den lov at blive, sætter den sig
// efter 6 s, og ledningen bærer det halve, til gæsten går efter 15 s. Gæsten vælger en ledning med
// god luft og går selv, hvis ledningen får travlt, så den alene aldrig mørklægger en by eller vælter
// en ledning. Den koster intet af direktørens budget og tæller ikke med som trussel.

import { anyFrontUnderWay } from './events';
import { markDirty } from './flow';
import { heldLine } from './effects';
import { lineById } from './grid';
import { nextInt, pick } from './rng';
import type { LevelDef, Line, SimEvent, SmaaVariant, State, Threat } from './types';
import { seasonOf } from './weather';

/**
 * Gæsten lander først, når der har været roligt i 7 s, mindst 15 s efter den forrige, og så med 1
 * chance ud af 20 pr. tick. Playtest runde 3: med 5 s, 6 s og 1/10 kom der en stork hvert 10. sekund,
 * og tappene fyldte mere end nettet. Nu kommer der højst én hvert ca. 20. sekund i de stille stunder.
 * (10 s, 20 s og 1/30 var for lidt: så stod bane 1 stille i halvdelen af tiden.)
 */
export const SMAA_CALM_TICKS = 70;
export const SMAA_GAP_TICKS = 150;
export const SMAA_CHANCE = 20;
/** Gæsten sætter sig efter 6 s og går efter 15 s. */
export const SMAA_BITE_TICKS = 60;
export const SMAA_LEAVE_TICKS = 150;
/** Kun ledninger med højst 45 % belastning får besøg. */
export const SMAA_MAX_LOAD_PM = 450;
/** Når den halverede ledning når 80 %, går gæsten. */
export const SMAA_RELEASE_PM = 800;

const VARIANTS: readonly SmaaVariant[] = ['stork', 'drage', 'ko'];
const WINTER_VARIANTS: readonly SmaaVariant[] = ['stork', 'drage', 'ko', 'juletrae'];

/** Gæster på kortet lige nu (højst én). */
export function smaaGuests(state: State): Threat[] {
  return state.threats.filter((t) => t.kind === 'smaa');
}

/** Ledninger, en gæst kan lande på: hele land-ledninger med god luft, ikke udlandsforbindelser. */
export function smaaLines(state: State): Line[] {
  return state.lines.filter(
    (l) =>
      l.status === 'ok' &&
      !l.halfDown &&
      l.fixedCapMw === 0 &&
      (l.medium === 'air' || l.medium === 'cable') &&
      l.loadPm <= SMAA_MAX_LOAD_PM &&
      !heldLine(state, l.id),
  );
}

/** Må en gæst lande nu (før terningen)? */
export function smaaAllowed(state: State, level: LevelDef): boolean {
  if (state.phase !== 'playing') return false;
  if (state.director.calmTicks < SMAA_CALM_TICKS) return false;
  if (state.clockTick - state.director.smaaLeftAt < SMAA_GAP_TICKS) return false;
  if (state.threats.length > 0) return false; // ingen trussel i varsel eller gang, og ingen gæst i forvejen
  for (const n of state.nodes) if (n.city && n.active && !n.phantom && n.city.clock > 0) return false;
  if (anyFrontUnderWay(state)) return false;
  // Bane 1: først når øvelsesstormen er overstået.
  if (state.director.planned.some((p) => p.tutorial)) return false;
  if (level.number === 1 && !state.stats.threatsFaced.some((f) => f.kind === 'storm')) return false;
  return true;
}

function leave(state: State, t: Threat, events: SimEvent[]): void {
  t.shooed = true;
  t.phase = 'active';
  t.endAt = state.clockTick + 20;
  markDirty(state);
  events.push({ t: 'smaa_left', tick: state.tick, threat: t.id, line: t.targetLine });
}

/** Land en gæst på en tilfældig ledning med god luft (eller netop `lineId`, til test og scripts). */
export function landSmaa(state: State, events: SimEvent[], lineId?: string): Threat | null {
  const rng = state.rng.smaa;
  const lines = smaaLines(state);
  const line = lineId !== undefined ? lines.find((l) => l.id === lineId) : lines.length > 0 ? pick(rng, lines) : undefined;
  if (!line) return null;
  const variant = pick(rng, seasonOf(state.clockTick) === 3 ? WINTER_VARIANTS : VARIANTS);
  const now = state.clockTick;
  const t: Threat = {
    id: `T${state.nextThreatId++}`,
    kind: 'smaa',
    phase: 'warning',
    scripted: false,
    warnAt: now,
    hitAt: now + SMAA_BITE_TICKS,
    endAt: now + SMAA_LEAVE_TICKS,
    trackY: 0,
    trackHalf: 0,
    strength: 1,
    targetLine: line.id,
    atRisk: [line.id],
    shooed: false,
    hits: [],
    variant,
  };
  state.threats.push(t);
  events.push({ t: 'smaa', tick: state.tick, threat: t.id, variant, line: line.id, biteAt: t.hitAt, leaveAt: t.endAt });
  return t;
}

/**
 * Ét tick for småt: gæsterne sætter sig og går, og en ny lander måske. Kører efter direktøren (som
 * tæller roen) og før flowet.
 */
export function updateSmaa(state: State, level: LevelDef, events: SimEvent[]): void {
  let gone = false;
  for (const t of state.threats) {
    if (t.kind !== 'smaa') continue;
    const line = lineById(state, t.targetLine);
    if (state.clockTick >= t.endAt) {
      // Bed den tidligt hjem (Næste år kan springe tiden frem), eller tiden er gået.
      if (t.phase === 'active' && !t.shooed) markDirty(state);
      t.phase = 'done';
      state.director.smaaLeftAt = state.clockTick;
      events.push({ t: 'threat_done', tick: state.tick, threat: t.id, kind: t.kind });
      gone = true;
      continue;
    }
    if (t.shooed) continue;
    if (!line || line.status !== 'ok') {
      leave(state, t, events);
      continue;
    }
    if (t.phase === 'warning' && state.clockTick >= t.hitAt) {
      // Den halve ledning skal kunne bære det, den bærer nu, med luft. Ellers flyver gæsten videre.
      if (line.loadPm * 2 >= SMAA_RELEASE_PM) {
        leave(state, t, events);
        continue;
      }
      t.phase = 'active';
      t.atRisk = [];
      markDirty(state);
      events.push({ t: 'smaa_bite', tick: state.tick, threat: t.id, line: line.id });
    }
  }
  if (gone) state.threats = state.threats.filter((t) => t.phase !== 'done');

  if (!state.rng.smaa || !smaaAllowed(state, level)) return;
  if (nextInt(state.rng.smaa, 1, SMAA_CHANCE) !== 1) return;
  landSmaa(state, events);
}

/**
 * Efter flowet: får en halveret ledning travlt (80 %), går gæsten med det samme. Returnerer true, så
 * kalderen regner flowet igen, før byernes ure ser på det. Så koster gæsten aldrig en by strøm.
 */
export function releaseSmaa(state: State, events: SimEvent[]): boolean {
  let released = false;
  for (const t of state.threats) {
    if (t.kind !== 'smaa' || t.phase !== 'active' || t.shooed) continue;
    const line = lineById(state, t.targetLine);
    if (line && line.loadPm < SMAA_RELEASE_PM) continue;
    leave(state, t, events);
    released = true;
  }
  return released;
}
